<template>
  <button
    v-if="isDesktopVisible && !isMobile"
    class="desktop-overlay"
    type="button"
    aria-label="Закрыть поиск"
    :style="desktopOverlayStyle"
    @click="clearSearch"
  />

  <button
    v-if="isMobileVisible && isMobile"
    class="mobile-overlay"
    type="button"
    aria-label="Закрыть поиск"
    @click="closeMobileSearch"
  />

  <div
    ref="searchContainer"
    v-click-outside:global-search-input="onOutsideClick"
    class="header-global-search"
    :class="{ '-mobile': isMobile, visible: isVisible }"
  >
    <div v-if="isMobile" class="mobile-header">
      <p class="mobile-title">Поиск</p>

      <button
        class="close-button"
        type="button"
        aria-label="Закрыть поиск"
        @click="closeMobileSearch"
      >
        <span class="icon">
          <BaseIconSvoi icon="essential/close" size="full" />
        </span>
      </button>
    </div>

    <BaseSearchWithButton
      v-if="isMobile"
      input-id="mobile-search-input"
      :value="query"
      @update:value="setQuery"
      @on-search="submitSearch"
    />

    <div class="results-scroll">
      <HeaderGlobalSearchResults
        :items="items"
        :query="query"
        :show-no-results="showNoResults"
        :is-mobile="isMobile"
        @select="openResultLink"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import { BaseSearchWithButton } from '~/components/base-ui/searches'
  import { useBreakpoint } from '~/composables/use-breakpoint'
  import { useGlobalSearch } from '~/composables/global-search/use-global-search'
  import HeaderGlobalSearchResults from './HeaderGlobalSearchResults.vue'

  const DESKTOP_PANEL_RADIUS = 16

  const { isMobile } = useBreakpoint()
  const searchContainer = ref<HTMLElement | null>(null)
  const desktopOverlayTop = ref(0)

  const {
    query,
    items,
    showNoResults,
    isDesktopVisible,
    isMobileVisible,
    setQuery,
    clearSearch,
    closeMobileSearch,
    openResultLink,
    submitSearch,
  } = useGlobalSearch()

  const isVisible = computed(() =>
    isMobile.value ? isMobileVisible.value : isDesktopVisible.value
  )

  const desktopOverlayStyle = computed(() => ({
    top: `${Math.max(0, desktopOverlayTop.value - DESKTOP_PANEL_RADIUS)}px`,
  }))

  const updateDesktopOverlayTop = () => {
    if (!searchContainer.value) {
      return
    }

    desktopOverlayTop.value = searchContainer.value.getBoundingClientRect().bottom
  }

  const toggleBodyScroll = (isLocked: boolean) => {
    if (import.meta.client) {
      document.body.style.overflow = isLocked ? 'hidden' : ''
    }
  }

  watch([isVisible, showNoResults, () => Object.keys(items.value).length], () => {
    if (isVisible.value && !isMobile.value) {
      void nextTick(updateDesktopOverlayTop)
    }
  })

  watch(
    () => isMobile.value && isMobileVisible.value,
    value => toggleBodyScroll(value),
    { immediate: true }
  )

  onMounted(() => {
    window.addEventListener('resize', updateDesktopOverlayTop)
    window.addEventListener('scroll', updateDesktopOverlayTop, { passive: true })
  })

  onBeforeUnmount(() => {
    toggleBodyScroll(false)
    window.removeEventListener('resize', updateDesktopOverlayTop)
    window.removeEventListener('scroll', updateDesktopOverlayTop)
  })

  const onOutsideClick = () => {
    if (isVisible.value && !isMobile.value) {
      clearSearch()
    }
  }
</script>

<style lang="scss" scoped>
  .desktop-overlay {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 59;
    border: none;
    padding: 0;
    background: rgb(0 0 0 / 25%);
  }

  .mobile-overlay {
    position: fixed;
    inset: 0;
    z-index: 69;
    border: none;
    padding: 0;
    background: rgb(0 0 0 / 25%);
  }

  .header-global-search {
    overflow: hidden;
    overflow-y: auto;
    position: absolute;
    z-index: 60;
    left: 0;
    display: grid;
    gap: 12px;
    justify-content: center;
    width: 100%;
    height: 0;
    padding: 0;
    background: $color-default-white;
    border-radius: 0 0 16px 16px;
    transition: $trans-default;

    &.visible {
      height: auto;
      max-height: 60vh;
      padding: 40px 0 24px;
    }

    > .results-scroll {
      display: contents;
    }

    &.-mobile {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 70;
      @extend %flex-column;

      justify-content: initial;
      overflow: hidden;
      border-radius: 20px 20px 0 0;

      &.visible {
        max-height: calc(100vh - 60px);
        padding: 16px;
        box-shadow: 0 -8px 30px rgb(0 0 0 / 15%);
      }

      > .mobile-header,
      > :deep(.base-search-with-button) {
        flex-shrink: 0;
      }

      > .results-scroll {
        display: block;
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
      }

      > .mobile-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      > .mobile-header > .mobile-title {
        @extend %font-16, %font-weight-500;
      }

      > .mobile-header > .close-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        border-radius: 50%;
        background: $main-back-color;

        @include hover-focus {
          cursor: pointer;
        }
      }

      > .mobile-header > .close-button > .icon {
        display: flex;
        width: 20px;
        height: 20px;
      }
    }
  }
</style>
